import { useState, type ReactNode } from 'react';
import { UMLayerGroup, UMLegendItem, UMSidePanel, UMTag, type UMLegendItemProps } from './UMSidePanel';
import { UMR } from './referenceTokens';

export interface UMLayerGroupSpec {
  id: string;
  label: string;
  defaultChecked?: boolean;
  defaultOpen?: boolean;
  hasMenu?: boolean;
  tag?: string;
  legend?: UMLegendItemProps[];
}

/**
 * "Couches de données" panel content as rendered in production (default state, measured).
 * Groups without a legend are collapsed in production; their content has not been captured.
 */
export const UM_LAYER_CATALOG: UMLayerGroupSpec[] = [
  {
    id: 'buildings',
    label: 'Bâtiments',
    defaultChecked: true,
    defaultOpen: true,
    hasMenu: true,
    tag: 'Conso. chaud',
    legend: [
      { label: '≤ 300 MWhu', color: UMR.map.consumption[0] },
      { label: '≤ 600 MWhu', color: UMR.map.consumption[1] },
      { label: '≤ 1000 MWhu', color: UMR.map.consumption[2] },
      { label: '+ 1000 MWhu', color: UMR.map.consumption[3] },
      { label: 'Réseau', color: UMR.map.pipe },
    ],
  },
  {
    id: 'energy-networks',
    label: 'Réseaux énergétiques',
    defaultChecked: true,
    defaultOpen: true,
    legend: [
      { label: 'Canalisations RCU/RFU', color: UMR.map.pipe, kind: 'visibility' },
      { label: 'Réseaux existants', color: UMR.map.pipe, kind: 'line', sub: true },
      { label: 'Réseau GRDF', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Réseau ENEDIS', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Zones de classement RCU/RFU', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Réseaux en construction', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Zones de réseaux en construction', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Périmètre contrat (DSP)', color: UMR.logoText, kind: 'visibility', disabled: true },
      { label: 'Postes de livraison', color: UMR.map.pipe, kind: 'visibility', disabled: true },
      { label: 'Centrales', color: UMR.map.pipe, kind: 'visibility' },
      { label: 'Centrales existantes', color: UMR.map.pipe, sub: true, disabled: true },
    ],
  },
  { id: 'consumption', label: 'Consommation' },
  { id: 'energy-potential', label: 'Potentiel énergétique' },
  { id: 'regulatory', label: 'Périmètres réglementaires' },
  { id: 'territorial', label: 'Couches territoriales', hasMenu: true },
  { id: 'climate', label: 'Climat / environnement' },
  { id: 'my-selections', label: 'Mes sélections' },
];

export interface UMLayersPanelProps {
  onClose?: () => void;
  /** Override or extend the production catalog (e.g. add a prototype-specific group). */
  groups?: UMLayerGroupSpec[];
  /** Controlled checked state by group id. Uncontrolled when omitted. */
  checked?: Record<string, boolean>;
  onCheckedChange?: (id: string, checked: boolean) => void;
  /** Extra content per group id, rendered under the legend. */
  renderGroupExtra?: (id: string) => ReactNode;
}

/** Production "Couches de données" panel, ready to drop into UMAppShell.leftPanel. */
export function UMLayersPanel({ onClose, groups = UM_LAYER_CATALOG, checked, onCheckedChange, renderGroupExtra }: UMLayersPanelProps) {
  const [local, setLocal] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map((g) => [g.id, !!g.defaultChecked])),
  );
  const state = checked ?? local;
  return (
    <UMSidePanel title="Couches de données" onClose={onClose}>
      {groups.map((group) => (
        <UMLayerGroup
          key={group.id}
          label={group.label}
          checked={!!state[group.id]}
          onCheckedChange={(value) => {
            if (!checked) setLocal((prev) => ({ ...prev, [group.id]: value }));
            onCheckedChange?.(group.id, value);
          }}
          defaultOpen={group.defaultOpen}
          onMenu={group.hasMenu ? () => undefined : undefined}
          tag={group.tag ? <UMTag>{group.tag}</UMTag> : undefined}
        >
          {(group.legend?.length || renderGroupExtra?.(group.id)) ? (
            <>
              {group.legend?.map((item) => <UMLegendItem key={item.label} {...item} />)}
              {renderGroupExtra?.(group.id)}
            </>
          ) : undefined}
        </UMLayerGroup>
      ))}
    </UMSidePanel>
  );
}
